import { defineStore } from 'pinia';
import { computed, ref, shallowRef, type ShallowRef } from 'vue';
import { ErpatApiError } from '@erpat/sdk';
import { securityPinService } from '@/services/erpat';
import type { SecurityPinPolicyVM, SecurityPinStatusVM } from '@/services/api/models';
import { getSessionEpoch } from '@/services/api/erpat-client';

/**
 * A group of confidential screens that share ONE unlock — unlocking on either
 * screen of the group shows the figures on both, and leaving the group re-locks
 * it (`router/confidential-scope.ts`).
 *
 * - `payslips` — the Payslips list and Payslip Preview: one unlock covers list →
 *   preview until the user leaves the payslip flow.
 */
export type ConfidentialScope = 'payslips';

/**
 * The caller's Security PIN state, and which confidential scopes are unlocked.
 *
 * WHAT IT HOLDS: the server-derived status (`available`, `enabled`, `locked`,
 * attempts, policy) and a set of unlocked scope names. It NEVER holds a PIN, a
 * password, a hash or a hash length — the prompt hands the typed secret straight
 * to the request and drops it.
 *
 * `supported` is a tri-state, settled like `approvals.store.ts` settles access:
 *
 *   - `true`  — the backend answered `GET /me/security-pin` (whatever it said);
 *   - `false` — it 404'd: an ERPat that predates the Security PIN. Every prompt
 *               stays on the password (and the legacy check);
 *   - `null`  — not settled. A transport failure, a 5xx, or the CI3 install
 *               redirect answering HTML is "don't know", and latching it would
 *               hide the PIN for the rest of the session over one dropped
 *               request. The next surface that asks re-probes.
 *
 * Two guards keep a stale answer from winning:
 *
 *   - a GENERATION counter, bumped by `refresh()`, `adopt()` and `reset()`: a
 *     status read that was already in flight when a PIN was just saved (and
 *     adopted) lands after it and must not overwrite it;
 *   - the SESSION EPOCH (`getSessionEpoch()`), captured before every await: a
 *     read that resolves after sign-out must not repopulate state for whoever
 *     signs in next.
 */
export const useSecurityStore = defineStore('security', () => {
  const status: ShallowRef<SecurityPinStatusVM | null> = shallowRef<SecurityPinStatusVM | null>(null);
  /** See the store docblock: true / false (404) / null (not settled). */
  const supported = ref<boolean | null>(null);
  /** True once the status read settled with a REAL answer (a 200 or a 404). */
  const loaded = ref(false);
  /**
   * Scopes the user has unlocked. A shallowRef REASSIGNED on every change,
   * never mutated in place, so every reader re-renders.
   */
  const unlockedScopes: ShallowRef<ReadonlySet<ConfidentialScope>> = shallowRef<ReadonlySet<ConfidentialScope>>(
    new Set<ConfidentialScope>(),
  );

  /** The server's PIN rules, when the status has been read. */
  const policy = computed((): SecurityPinPolicyVM | null => status.value?.policy ?? null);

  /**
   * The PIN can be offered on a prompt right now: the backend supports it, the
   * install can check PINs, one is set, and it is not locked. Anything short of
   * that asks for the password, which is always accepted.
   */
  const pinUsable = computed((): boolean => {
    const s = status.value;
    return supported.value === true && !!s && s.available && s.enabled && !s.locked;
  });

  // Collapse overlapping reads: a prompt and the drawer can ask on the same tick.
  let inFlight: Promise<void> | null = null;
  let generation = 0;

  function load(): Promise<void> {
    const epoch = getSessionEpoch();
    const gen = generation;

    const run = (async () => {
      try {
        const vm = await securityPinService.status();
        // Teardown wins: drop a result that resolved after sign-out, or after a
        // refresh()/adopt()/reset() superseded this read.
        if (getSessionEpoch() !== epoch || gen !== generation) return;
        status.value = vm;
        supported.value = true;
        loaded.value = true;
      } catch (err) {
        if (getSessionEpoch() !== epoch || gen !== generation) return;
        // Only a 404 is an answer ("this backend has no Security PIN"). Anything
        // else leaves the store unsettled — and keeps any status already known.
        if (err instanceof ErpatApiError && err.status === 404) {
          status.value = null;
          supported.value = false;
          loaded.value = true;
        }
      }
    })();

    inFlight = run;
    void run.finally(() => {
      if (inFlight === run) inFlight = null;
    });
    return run;
  }

  /**
   * Read the status once per session. Never rejects, so callers can `void` it.
   */
  function ensureLoaded(): Promise<void> {
    if (loaded.value) return Promise.resolve();
    if (inFlight) return inFlight;
    return load();
  }

  /**
   * Re-read the status from scratch — after a session starts, and whenever a
   * prompt learns the PIN changed under it (locked, removed, unavailable). The
   * last known status stays visible until the answer lands. Never rejects.
   */
  function refresh(): Promise<void> {
    generation += 1;
    inFlight = null;
    return load();
  }

  /**
   * Take a status the server just returned with a successful save, remove or
   * verify. Supersedes any read still in flight, so that read cannot land later
   * and overwrite the newer answer.
   */
  function adopt(vm: SecurityPinStatusVM): void {
    generation += 1;
    inFlight = null;
    status.value = vm;
    supported.value = true;
    loaded.value = true;
  }

  function isScopeUnlocked(scope: ConfidentialScope): boolean {
    return unlockedScopes.value.has(scope);
  }

  function unlockScope(scope: ConfidentialScope): void {
    if (unlockedScopes.value.has(scope)) return;
    unlockedScopes.value = new Set<ConfidentialScope>(unlockedScopes.value).add(scope);
  }

  function lockScope(scope: ConfidentialScope): void {
    if (!unlockedScopes.value.has(scope)) return;
    const next = new Set<ConfidentialScope>(unlockedScopes.value);
    next.delete(scope);
    unlockedScopes.value = next;
  }

  /**
   * Drop everything on sign-out: the next account must read its own status, and
   * no unlock may carry over to it. A read still in flight is abandoned.
   */
  function reset(): void {
    generation += 1;
    inFlight = null;
    status.value = null;
    supported.value = null;
    loaded.value = false;
    unlockedScopes.value = new Set<ConfidentialScope>();
  }

  return {
    status,
    supported,
    loaded,
    policy,
    pinUsable,
    unlockedScopes,
    ensureLoaded,
    refresh,
    adopt,
    reset,
    isScopeUnlocked,
    unlockScope,
    lockScope,
  };
});
